/**
 * ParityStatusPanel — v6.32.135
 *
 * Always-on dashboard for the BLM parity invariant.
 *
 * The invariant: every BLM PDF in GridFS should reflect the current state
 * of its source `questions` documents. v6.32.135 backend writes a
 * parity_snapshots row at each successful press_regen (hashing all the
 * source fields the PDF was baked from), and this panel calls
 * GET /api/content-auditor/parity-status to compare hashes live.
 *
 * Shows:
 *   - One big aggregate chip ("112/112 in sync" or "3 stale + 2 never-baked")
 *   - An expandable list of drifted filenames with drift_count and first
 *     few drifted question IDs
 *   - One-click "Regen Stale Only" button when any drift exists
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  ShieldCheck, AlertTriangle, RefreshCw, Loader2, Zap, ChevronDown, ChevronRight,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authCfg = () => {
  const t = localStorage.getItem('mosaic_session_token') || '';
  return { headers: { Authorization: `Bearer ${t}` } };
};

const POLL_MS = 60000;  // 60s — parity is a slow-moving signal

const ParityStatusPanel = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [regenBusy, setRegenBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [filterStatus, setFilterStatus] = useState('drifted');  // drifted | all
  const timerRef = useRef(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/content-auditor/parity-status`, authCfg());
      setReport(data);
    } catch (e) {
      // Silent — panel just won't update
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReport();
    timerRef.current = setInterval(fetchReport, POLL_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [fetchReport]);

  const triggerRegenStale = async () => {
    setRegenBusy(true);
    try {
      // v6.35.35 — Two-phase: (1) dry-probe (start_regen=false) so the
      // operator sees the count + cost estimate before committing; (2)
      // on confirm, real call with start_regen=true that fires the regen
      // server-side. Replaces the legacy delegate-to-/regen-press-pdfs
      // dance that POC B showed was dropping operators 100% of the time.
      const probe = await axios.post(
        `${API}/content-auditor/regen-stale`,
        { confirm: true, start_regen: false },
        authCfg(),
      );
      if (!probe.data.ok) {
        toast.error(`Regen-stale failed: ${probe.data.error || 'unknown error'}`);
        return;
      }
      const targets = probe.data.target_filenames || [];
      if (targets.length === 0) {
        toast.success('All BLM PDFs already in sync. Nothing to regen.');
        return;
      }
      // eslint-disable-next-line no-alert
      if (!window.confirm(
        `Force-regenerate ${targets.length} BLM PDFs?\n\n` +
        `• ${probe.data.stale_count} stale + ${probe.data.never_baked_count} never-baked\n` +
        `• Estimated runtime: ~${Math.ceil(targets.length * 4)} seconds\n` +
        `• Audit-logged. Press PDFs chip will show live progress.\n\n` +
        `Continue?`
      )) return;
      // Commit — same endpoint, start_regen=true (default) actually fires it.
      const { data } = await axios.post(
        `${API}/content-auditor/regen-stale`,
        { confirm: true, start_regen: true },
        authCfg(),
      );
      if (data.regen_started) {
        toast.success(
          `Force-regenerating ${targets.length} stale BLMs (bundle ${data.bundle_id}). Do not close this tab.`,
          { duration: 10000 }
        );
      } else {
        toast.warning(
          `Regen list computed (${targets.length} targets) but worker did not start. Check Maintenance logs.`
        );
      }
      setTimeout(fetchReport, 3000);
    } catch (e) {
      toast.error(`Regen failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRegenBusy(false);
    }
  };

  // v6.32.140 — Smart Watermark Resume. Regens ONLY the BLMs whose
  // parity_snapshots row is missing or on an older parity_hash_version.
  // Solves the production bug where /regen-press-pdfs with
  // force_regen_all=true couldn't complete inside the pod lifetime
  // (worker died at 17/112 with 700s stale heartbeat). Each click
  // shrinks the target list — convergent retry.
  const triggerRegenUnwatermarked = async () => {
    setRegenBusy(true);
    try {
      // Step 1 — dry-run to learn the count without starting the regen
      // (so the confirm dialog can show real numbers).
      const dry = await axios.post(
        `${API}/content-auditor/regen-unwatermarked`,
        { confirm: false, dry_run: true },
        authCfg(),
      );
      const n = (dry.data && dry.data.unwatermarked_count) || 0;
      if (n === 0) {
        toast.success('All BLM press PDFs already carry the current parity watermark.');
        return;
      }
      const eta = Math.ceil(n * 4);
      // eslint-disable-next-line no-alert
      if (!window.confirm(
        `Smart Watermark Resume: regen ${n} BLM PDF${n === 1 ? '' : 's'} ` +
        `whose parity watermark is missing or out-of-date. ` +
        `Already-watermarked PDFs are NOT touched. ` +
        `If the pod dies mid-run, re-click — the target list shrinks. ` +
        `ETA ~${eta} seconds. Continue?`
      )) return;
      const { data } = await axios.post(
        `${API}/content-auditor/regen-unwatermarked`,
        { confirm: true, dry_run: false },
        authCfg(),
      );
      if (!data.ok) {
        toast.error(`Regen-unwatermarked failed: ${data.error || 'unknown error'}`);
        return;
      }
      toast.success(
        `Smart Watermark Resume started for ${data.unwatermarked_count} BLMs. ` +
        `(Already watermarked: ${data.watermarked_count}/${data.expected_total}.)`
      );
      setTimeout(fetchReport, 3000);
    } catch (e) {
      toast.error(`Regen-unwatermarked failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRegenBusy(false);
    }
  };

  if (!report) {
    return (
      <div className="rounded border border-gray-200 bg-gray-50 p-3 text-xs text-gray-500" data-testid="parity-panel-loading">
        <Loader2 size={12} className="inline animate-spin mr-1" />
        Loading parity status…
      </div>
    );
  }

  const s = report.summary || {};
  const inSync = (s.regen_required_count || 0) === 0;
  const driftCount = (s.stale || 0) + (s.never_baked || 0);
  const banner = inSync
    ? 'border-emerald-300 bg-emerald-50 text-emerald-900'
    : 'border-amber-300 bg-amber-50 text-amber-900';

  const visibleRows = (report.by_filename || []).filter(r => {
    if (filterStatus === 'drifted') return r.status === 'stale' || r.status === 'never_baked';
    return true;
  });

  return (
    <div className={`rounded border ${banner} p-3`} data-testid="parity-status-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          {inSync
            ? <ShieldCheck size={18} className="text-emerald-700" />
            : <AlertTriangle size={18} className="text-amber-700" />}
          <div>
            <div className="text-sm font-bold">
              BLM ↔ Digital Parity {inSync ? '· In Sync' : `· ${driftCount} drift${driftCount === 1 ? '' : 's'}`}
            </div>
            <div className="text-[11px] opacity-80 mt-0.5" data-testid="parity-counters">
              <span className="font-mono">
                {s.in_sync || 0}/{s.total || 0} in sync
              </span>
              {(s.stale || 0) > 0 && (
                <span className="ml-2 px-1.5 py-0.5 bg-rose-100 border border-rose-300 rounded text-[10px] text-rose-800 font-semibold">
                  {s.stale} stale
                </span>
              )}
              {(s.never_baked || 0) > 0 && (
                <span className="ml-2 px-1.5 py-0.5 bg-slate-100 border border-slate-300 rounded text-[10px] text-slate-700 font-semibold">
                  {s.never_baked} never baked
                </span>
              )}
              {/* v6.32.139 — Verified-Watermarked Coverage badge. Lights green
                  once every expected BLM in parity_snapshots carries the
                  current PARITY_HASH_VERSION ("v1"). Gives the auditor a
                  one-glance proof of: "every printed booklet in the wild
                  can be traced back to its source snapshot via the footer
                  watermark." */}
              {report.watermark_coverage && (() => {
                const wc = report.watermark_coverage;
                const isComplete = !!wc.is_complete;
                const cls = isComplete
                  ? 'bg-emerald-100 border-emerald-400 text-emerald-800'
                  : 'bg-amber-50 border-amber-300 text-amber-800';
                const title = isComplete
                  ? `All ${wc.total} BLM press PDFs carry a ${wc.version} parity watermark in their footers — every printed booklet in the wild can be audited back to its source snapshot via /api/content-auditor/parity-lookup.`
                  : `${wc.watermarked} of ${wc.total} BLM press PDFs carry the ${wc.version} parity watermark. Re-bake the remaining ${Math.max(0, wc.total - wc.watermarked)} (use the [Force overwrite all] checkbox on Regenerate Press PDFs) so every printed booklet can be audited.`;
                return (
                  <span
                    className={`ml-2 px-1.5 py-0.5 border rounded text-[10px] font-semibold inline-flex items-center gap-1 ${cls}`}
                    data-testid="parity-watermark-coverage-badge"
                    title={title}
                  >
                    {isComplete && <ShieldCheck size={10} />}
                    {wc.watermarked}/{wc.total} watermarked {wc.version}
                  </span>
                );
              })()}
              <span className="ml-2 text-[10px] opacity-60">
                · hash {report.parity_hash_version || 'v?'}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchReport}
            disabled={loading}
            data-testid="parity-refresh-btn"
            title="Recompute parity status now"
          >
            {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            <span className="ml-1 text-[11px]">Refresh</span>
          </Button>
          {!inSync && (
            <Button
              size="sm"
              onClick={triggerRegenStale}
              disabled={regenBusy}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="parity-regen-stale-btn"
              title="Regenerate ONLY the stale + never-baked BLMs. Skips the in-sync ones to save ~30 min per cycle."
            >
              {regenBusy
                ? <><Loader2 size={12} className="mr-1 animate-spin" /> Queuing…</>
                : <><Zap size={12} className="mr-1" /> Regen Stale Only ({driftCount})</>}
            </Button>
          )}
          {/* v6.32.140 — Smart Watermark Resume. Shown whenever the
              emerald coverage badge is NOT complete. Click → POST
              /regen-unwatermarked with the filtered list of BLMs that
              don't yet carry the current parity_hash_version. Each
              successful PDF writes a v1 snapshot, so the target list
              shrinks monotonically across retries. Solves the prod
              pod-death bug exposed on 2026-05-12. */}
          {(() => {
            const wc = report.watermark_coverage;
            if (!wc || wc.is_complete) return null;
            const missing = Math.max(0, (wc.total || 0) - (wc.watermarked || 0));
            return (
              <Button
                size="sm"
                onClick={triggerRegenUnwatermarked}
                disabled={regenBusy}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="parity-regen-unwatermarked-btn"
                title="Smart Watermark Resume — regen ONLY the BLMs that don't yet carry the current parity watermark. Already-watermarked PDFs are NOT re-baked. If the pod dies mid-run, re-click — the target list shrinks each time, so the rollout always converges."
              >
                {regenBusy
                  ? <><Loader2 size={12} className="mr-1 animate-spin" /> Queuing…</>
                  : <><ShieldCheck size={12} className="mr-1" /> Regen Unwatermarked ({missing})</>}
              </Button>
            );
          })()}
          <button
            type="button"
            onClick={() => setExpanded(v => !v)}
            className="text-[11px] underline opacity-70 hover:opacity-100"
            data-testid="parity-toggle-detail"
          >
            {expanded ? <ChevronDown size={14} className="inline" /> : <ChevronRight size={14} className="inline" />}
            {expanded ? 'Hide detail' : 'Show detail'}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-current border-opacity-20" data-testid="parity-detail">
          <div className="flex items-center gap-2 text-[10px] mb-2">
            <span className="opacity-70">Show:</span>
            <button
              type="button"
              onClick={() => setFilterStatus('drifted')}
              className={`px-1.5 py-0.5 rounded border ${filterStatus === 'drifted' ? 'bg-white border-current font-semibold' : 'border-transparent opacity-60'}`}
              data-testid="parity-filter-drifted"
            >
              Drifted only ({driftCount})
            </button>
            <button
              type="button"
              onClick={() => setFilterStatus('all')}
              className={`px-1.5 py-0.5 rounded border ${filterStatus === 'all' ? 'bg-white border-current font-semibold' : 'border-transparent opacity-60'}`}
              data-testid="parity-filter-all"
            >
              All ({s.total || 0})
            </button>
          </div>
          <div className="max-h-72 overflow-y-auto bg-white rounded border border-current border-opacity-10">
            <table className="w-full text-[11px]">
              <thead className="bg-current bg-opacity-5">
                <tr className="text-left">
                  <th className="px-2 py-1 font-semibold">Filename</th>
                  <th className="px-2 py-1 font-semibold">Status</th>
                  <th className="px-2 py-1 font-semibold">Drift</th>
                  <th className="px-2 py-1 font-semibold">Baked at</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.slice(0, 200).map((r, i) => {
                  const colour = r.status === 'in_sync' ? 'text-emerald-700'
                    : r.status === 'stale' ? 'text-rose-700'
                    : r.status === 'never_baked' ? 'text-slate-600'
                    : 'text-gray-500';
                  return (
                    <tr key={r.filename} className="border-t border-current border-opacity-10" data-testid={`parity-row-${i}`}>
                      <td className="px-2 py-1 font-mono text-[10px]">{r.filename}</td>
                      <td className={`px-2 py-1 font-semibold ${colour}`}>{r.status}</td>
                      <td className="px-2 py-1">
                        {r.drift_count > 0 ? (
                          <span className="text-rose-700">
                            {r.drift_count} question{r.drift_count === 1 ? '' : 's'}
                            {r.drift && r.drift.length > 0 && (
                              <span className="text-[10px] opacity-60 ml-1">
                                ({r.drift.slice(0, 3).map(d => d.reason).join(', ')})
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="opacity-50">—</span>
                        )}
                      </td>
                      <td className="px-2 py-1 text-[10px] opacity-70">
                        {r.baked_at ? new Date(r.baked_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                      </td>
                    </tr>
                  );
                })}
                {visibleRows.length === 0 && (
                  <tr><td colSpan={4} className="px-2 py-3 text-center opacity-60">
                    {filterStatus === 'drifted' ? 'No drifts. All 112 BLMs in sync.' : 'No filenames.'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          {visibleRows.length > 200 && (
            <div className="text-[10px] opacity-60 mt-1">Showing first 200 of {visibleRows.length}.</div>
          )}
        </div>
      )}

      {(s.never_baked || 0) === (s.total || 0) && (s.total || 0) > 0 && (
        <div className="mt-2 text-[10px] opacity-80">
          <strong>First-time setup:</strong> no parity snapshots exist yet. The next press-regen will write snapshots
          for every successful filename. Run a full regen once to establish baseline, then this panel will only
          highlight the BLMs that drift afterward.
        </div>
      )}
    </div>
  );
};

export default ParityStatusPanel;
